import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Ban } from "lucide-react";
import { toast } from "sonner";

const BlockedList = ({ blocks, onUnblock }) => {
  const [confirm, setConfirm] = useState(null);
  return (
    <>
      {blocks.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="blocked-senders-empty">You haven't blocked anyone.</p>
      ) : blocks.map((b) => (
        <div key={b.target_key} className="flex items-center justify-between py-1.5 text-sm" data-testid={`blocked-row-${b.target_key}`}>
          <div>
            <p className="text-zinc-800">{b.target_name || "Unknown sender"}</p>
            <p className="text-[11px] text-zinc-500">Blocked {new Date(b.created_at).toLocaleDateString()}</p>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setConfirm(b)}
                  data-testid={`unblock-${b.target_key}`}>Unblock</Button>
        </div>
      ))}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="unblock-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock {confirm?.target_name}?</AlertDialogTitle>
            <AlertDialogDescription>You'll see their messages again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="unblock-confirm" onClick={() => { onUnblock(confirm); setConfirm(null); }}>
              Unblock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const BlockedSendersCard = () => {
  const [blocks, setBlocks] = useState([]);
  const load = useCallback(async () => {
    try { setBlocks((await API.get("/moderation/blocks")).data.blocks || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unblock = async (b) => {
    try {
      await API.delete(`/moderation/blocks/${encodeURIComponent(b.target_key)}`);
      toast.success(`${b.target_name || "Sender"} unblocked`);
      load();
    } catch { toast.error("Could not unblock"); }
  };

  return (
    <Card className="border border-zinc-200" data-testid="blocked-senders-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Ban className="w-4 h-4 text-zinc-400" />
          <h3 className="text-sm font-heading font-semibold text-zinc-900">Blocked Users</h3>
        </div>
        <BlockedList blocks={blocks} onUnblock={unblock} />
      </CardContent>
    </Card>
  );
};

// Homeowner job-chat page: same list, authorized with the per-job link token.
export const HomeownerBlockedList = ({ requestId, token, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/moderation/public/${requestId}`;
  const load = useCallback(async () => {
    const res = await fetch(`${base}/blocks?t=${encodeURIComponent(token)}`);
    if (res.ok) setBlocks((await res.json()).blocks || []);
  }, [base, token]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const unblock = async (b) => {
    const res = await fetch(`${base}/blocks/${encodeURIComponent(b.target_key)}?t=${encodeURIComponent(token)}`, { method: "DELETE" });
    if (res.ok) { toast.success(`${b.target_name || "Sender"} unblocked`); load(); onChanged?.(); }
    else toast.error("Could not unblock");
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-[10px] text-zinc-400 hover:text-white"
              data-testid="homeowner-blocked-users">Blocked Users</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="homeowner-blocked-dialog">
          <DialogHeader>
            <DialogTitle>Blocked Users</DialogTitle>
            <DialogDescription>People you've blocked in this job chat.</DialogDescription>
          </DialogHeader>
          <BlockedList blocks={blocks} onUnblock={unblock} />
        </DialogContent>
      </Dialog>
    </>
  );
};
